import { cn, Input, InputProps } from '@heroui/react'
import type { FieldProps } from 'formik'
import { Field } from 'formik'
import { motion } from 'framer-motion'
import { AsYouType } from 'libphonenumber-js'

export interface PhoneFormikProps extends Omit<InputProps, 'classNames'> {
  label?: string
  placeholder?: string
  classNameContainer?: string
  variant?: 'bordered' | 'flat' | 'faded' | 'underlined'
  classNames?: {
    label?: string[]
    input?: string[]
    innerWrapper?: string
    inputWrapper?: string[]
  }
  labelPlacement?: 'outside' | 'inside' | 'outside-left'
  name: string
  isRequired?: boolean
}

const ONLY_ALLOWED = /^[\d\s()+-]*$/
const toE164 = (raw: string) => {
  if (!raw) return ''
  const digits = raw.replace(/\D/g, '')

  return digits ? `+${digits}` : ''
}
const isValidIntl = (e164: string) => {
  if (!e164 || e164[0] !== '+') return false
  const digits = e164.slice(1)

  // Total digits must be between 8-19 (1-4 for country code + 7-15 for national)
  if (digits.length < 8 || digits.length > 19) return false

  // Validate that we have a reasonable country code (1-4 digits) and national number (7-15 digits)
  // We need to check all possible splits to ensure at least one valid combination exists
  for (let ccLength = 1; ccLength <= 4; ccLength++) {
    const nationalLength = digits.length - ccLength

    if (nationalLength >= 7 && nationalLength <= 15) {
      return true
    }
  }

  return false
}
const formatIntlDisplay = (raw: string) => {
  if (!raw) return ''
  const hasPlus = raw.trim().startsWith('+')
  const digits = raw.replace(/\D/g, '')
  const input = hasPlus ? `+${digits}` : `+${digits}`

  return new AsYouType().input(input)
}

const PhoneFormik = ({
  classNameContainer,
  variant = 'bordered',
  classNames,
  labelPlacement = 'outside',
  name,
  isRequired = false,
  ...props
}: PhoneFormikProps) => {
  return (
    <Field name={name}>
      {({ field, form, meta }: FieldProps) => {
        const displayValue = formatIntlDisplay(field.value || '')

        return (
          <motion.div className={cn(classNameContainer)}>
            <Input
              {...field}
              fullWidth
              className="h-full"
              isRequired={isRequired}
              labelPlacement={labelPlacement}
              placeholder={props.placeholder || 'Enter phone number (e.g. +33 1 23 45 67 89)'}
              radius="sm"
              value={displayValue}
              variant={variant}
              classNames={{
                label: [
                  'text-gray-900',
                  'dark:text-foreground',
                  'group-data-[filled-within=true]:text-gray-900',
                  'dark:group-data-[filled-within=true]:text-white',
                  'group-data-[invalid=true]:text-danger',
                  'dark:group-data-[invalid=true]:text-danger',
                ],
                input: [
                  'bg-transparent',
                  'text-gray-900',
                  'dark:text-white',
                  'placeholder:text-gray-500',
                  'dark:placeholder:text-white/60',
                  'group-data-[invalid=true]:placeholder:text-danger',
                ],
                innerWrapper: 'bg-transparent',
                inputWrapper: [
                  'bg-gray-50',
                  'dark:bg-[#1e1e20]',
                  'border-gray-200',
                  'dark:border-default-600',
                  'hover:bg-gray-100',
                  'dark:hover:bg-[#1e1e20]/70',
                  'group-data-[focus=true]:bg-gray-100',
                  'dark:group-data-[focus=true]:bg-[#1e1e20]/50',
                  'group-data-[focus=true]:border-primary',
                  'dark:group-data-[focus=true]:border-primary',
                  '!cursor-text',
                ],
                ...classNames,
              }}
              onChange={e => {
                const raw = e.target.value

                // Allow clear & deletions
                if (!raw || raw.length < (field.value?.length || 0)) {
                  form.setFieldValue(name, raw)

                  return
                }

                if (!ONLY_ALLOWED.test(raw)) return

                // Limit total digits to 19 (maximum valid international phone number length)
                const digits = raw.replace(/\D/g, '')

                if (digits.length > 19) return

                // Store normalized E.164 in form state
                const e164 = toE164(raw)

                form.setFieldValue(name, e164)

                // Optionally set Formik error on-the-fly (keeps your validate schemas simple)
                if (isRequired && !e164) {
                  form.setFieldError(name, 'Phone number is required')
                } else if (e164 && !isValidIntl(e164)) {
                  form.setFieldError(name, 'Phone number is invalid')
                } else if (meta.error) {
                  form.setFieldError(name, undefined)
                }
              }}
              {...props}
            />
          </motion.div>
        )
      }}
    </Field>
  )
}

export default PhoneFormik
